import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, Trash2, RotateCcw, ArrowUpCircle, Sparkles, ArrowRight, Folder } from "lucide-react";
import { adminApi, clearSession, loadSession } from "./adminApi";
import SetupChecklistModal from "./SetupChecklistModal";
import TallyHealthMonitor from "./TallyHealthMonitor";
import CustomerLifecycleModal from "./CustomerLifecycleModal";
import CustomerUpgradeModal from "./CustomerUpgradeModal";
import CustomerContextModal from "./CustomerContextModal";
import SubscriptionManagement from "./SubscriptionManagement";

const shell = { minHeight: "100vh", background: "#F3F6FB", fontFamily: "-apple-system,Segoe UI,Roboto,sans-serif" };
const topBar = {
  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 8,
  background: "#0F1B2D", color: "white",
  position: "sticky", top: 0, zIndex: 10,
};
const container = { maxWidth: 1280, margin: "0 auto" };
const tableCard = {
  background: "white", borderRadius: 12, border: "1px solid #E5E7EB",
  overflow: "hidden", marginTop: 20,
};
const td = { padding: "10px 14px", fontSize: 12.5, color: "#0F1B2D", borderTop: "1px solid #F1F5F9", verticalAlign: "top" };
const th = { padding: "10px 14px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, textAlign: "left", borderBottom: "1px solid #E5E7EB", fontWeight: 700 };

function timeAgo(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const m = Math.floor((Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusBadge({ status }) {
  const map = {
    pending_otp:       { bg: "#FEE2E2", fg: "#B91C1C", label: "Pending OTP" },
    verified:          { bg: "#FEF3C7", fg: "#92400E", label: "Verified" },
    profile_complete:  { bg: "#DBEAFE", fg: "#1D4ED8", label: "Profile complete" },
    pending_setup:     { bg: "#FEF3C7", fg: "#92400E", label: "Pending setup" },
    active:            { bg: "#DCFCE7", fg: "#166534", label: "Active" },
  };
  const m = map[status] || { bg: "#F3F4F6", fg: "#374151", label: status || "—" };
  return <span style={{ background: m.bg, color: m.fg, padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>{m.label}</span>;
}

function SoftwarePills({ list }) {
  const items = Array.isArray(list) ? list.slice(0, 3) : [];
  if (!items.length) return <span style={{ color: "#9CA3AF" }}>—</span>;
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {items.map((s) => (
        <span key={s} style={{ background: "#EFF6FF", color: "#1D4ED8", borderRadius: 999, padding: "2px 8px", fontSize: 11 }}>{s}</span>
      ))}
      {Array.isArray(list) && list.length > 3 && (
        <span style={{ background: "#F3F4F6", color: "#6B7280", borderRadius: 999, padding: "2px 8px", fontSize: 11 }}>+{list.length - 3}</span>
      )}
    </div>
  );
}

const ADMIN_SECTIONS = [
  { id: "new-signups", title: "New signups", description: "Awaiting setup", tone: "amber" },
  { id: "all-customers", title: "All customers", description: "Customer records", tone: "blue" },
  { id: "funding", title: "C-ORE Fund — Funding Requests", description: "Funding advisory requests", tone: "sky" },
  { id: "subscriptions", title: "Subscription Management", description: "Plan catalogue and pricing", tone: "emerald" },
  { id: "deleted-customers", title: "Deleted customers", description: "Recoverable customer records", tone: "rose" },
  { id: "tally-health", title: "Tally Sync Health Monitor", description: "Live sync status", tone: "indigo" },
];

function FolderCard({ section, status, onOpen }) {
  const tones = {
    amber: "#B45309", blue: "#1D4ED8", sky: "#0369A1", emerald: "#047857", rose: "#B91C1C", indigo: "#4338CA", teal: "#0F766E",
  };
  return (
    <button
      data-testid={`admin-folder-${section.id}`}
      onClick={onOpen}
      type="button"
      style={{
        background: "white", border: "1px solid #DCE4EF", borderRadius: 10, minHeight: 150,
        padding: "20px", textAlign: "left", cursor: "pointer", color: "#0F1B2D",
        boxShadow: "0 1px 2px rgba(15,27,45,0.04)", transition: "transform 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}
      onMouseEnter={(event) => { event.currentTarget.style.transform = "translateY(-2px)"; event.currentTarget.style.borderColor = tones[section.tone]; event.currentTarget.style.boxShadow = "0 8px 18px rgba(15,27,45,0.08)"; }}
      onMouseLeave={(event) => { event.currentTarget.style.transform = "translateY(0)"; event.currentTarget.style.borderColor = "#DCE4EF"; event.currentTarget.style.boxShadow = "0 1px 2px rgba(15,27,45,0.04)"; }}
    >
      <div>
        <Folder size={18} color={tones[section.tone]} strokeWidth={1.8} aria-hidden="true" />
        <div style={{ marginTop: 14, fontSize: 16, lineHeight: 1.35, fontWeight: 750 }}>{section.title}</div>
        <div data-testid={`admin-folder-status-${section.id}`} style={{ marginTop: 6, fontSize: 12.5, color: "#64748B" }}>{status || section.description}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", color: tones[section.tone] }}><ArrowRight size={19} aria-hidden="true" /></div>
    </button>
  );
}

function AdminBreadcrumb({ sectionName, onHome }) {
  return (
    <div data-testid="admin-breadcrumb" style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 20, fontSize: 13, color: "#64748B" }}>
      <button data-testid="admin-breadcrumb-home" type="button" onClick={onHome}
        style={{ border: "none", background: "transparent", padding: 0, color: "#1E3A5F", fontWeight: 700, cursor: "pointer" }}>
        C-ORE Admin
      </button>
      <span aria-hidden="true">/</span>
      <span data-testid="admin-breadcrumb-current" style={{ color: "#475569", fontWeight: 600 }}>{sectionName}</span>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeSection, setActiveSection] = useState("home");
  const [allCustomers, setAllCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [now, setNow] = useState(new Date());
  const [page, setPage] = useState(1);
  const [activeCustomer, setActiveCustomer] = useState(null);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [lifecycle, setLifecycle] = useState(null); // { customer, mode }
  const [upgradeCustomer, setUpgradeCustomer] = useState(null);
  const [contextCustomer, setContextCustomer] = useState(null);
  const [fundingRequests, setFundingRequests] = useState([]);

  // Auth gate
  useEffect(() => {
    if (!loadSession()) navigate("/admin", { replace: true });
  }, [navigate]);

  const refresh = useCallback(async () => {
    const [s, c, d, f] = await Promise.all([
      adminApi.stats(),
      adminApi.listCustomers({}),
      adminApi.listCustomers({ include_deleted: true }),
      adminApi.fundingRequests(),
    ]);
    if (s.ok) setStats(s.data);
    if (c.ok) setAllCustomers(c.data?.customers || []);
    if (d.ok) setDeletedCustomers((d.data?.customers || []).filter((x) => x.deleted_at));
    if (f.ok) setFundingRequests(f.data?.requests || []);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh every 60 s.
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // IST clock — updates every second.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const signOut = () => { clearSession(); navigate("/admin"); };

  const newSignups = useMemo(
    () => allCustomers.filter((c) => ["profile_complete", "pending_setup"].includes(c.status)),
    [allCustomers]
  );

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    return allCustomers.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (!q) return true;
      const hay = `${c.name || ""} ${c.company_name || ""} ${c.phone || ""} ${c.email || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allCustomers, search, statusFilter]);

  const pageSize = 20;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [search, statusFilter]);

  // IST = UTC+5:30 — render the clock in IST regardless of host TZ.
  const istString = now.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: true });
  const activeSectionName = ADMIN_SECTIONS.find((section) => section.id === activeSection)?.title || "";
  const sectionStatus = {
    "new-signups": newSignups.length ? `${newSignups.length} awaiting setup` : "No signups awaiting setup",
    "all-customers": `${allCustomers.length} customer${allCustomers.length === 1 ? "" : "s"}`,
    funding: fundingRequests.length ? `${fundingRequests.length} funding request${fundingRequests.length === 1 ? "" : "s"}` : "No funding requests",
    subscriptions: stats?.active_customers != null ? `${stats.active_customers} active accounts` : "Plan catalogue and pricing",
    "deleted-customers": deletedCustomers.length ? `${deletedCustomers.length} recoverable record${deletedCustomers.length === 1 ? "" : "s"}` : "No deleted customers",
    "tally-health": `${allCustomers.length} customer${allCustomers.length === 1 ? "" : "s"} monitored`,
  };

  return (
    <div data-testid="admin-dashboard-page" style={shell}>
      <div style={topBar} className="px-4 tablet:px-5 desktop:px-6 py-3">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            C-ORE Admin
          </span>
          <span style={{ fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1 }}>ops console</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <span data-testid="admin-clock" className="hidden tablet:inline desktop:inline" style={{ fontSize: 12, color: "#cbd5e1" }}>{istString} IST</span>
          <button data-testid="admin-nav-expert" onClick={() => navigate("/admin/expert")}
            style={{ background: "transparent", color: "#93c5fd", border: "1px solid #93c5fd", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            C-ORE Expert →
          </button>
          <button data-testid="admin-signout" onClick={signOut}
            style={{ background: "transparent", color: "#10b981", border: "1px solid #10b981", borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <LogOut size={12} /> Sign Out
          </button>
        </div>
      </div>

      <div style={container} className="px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-6">
        {activeSection === "home" ? (
          <div data-testid="admin-folder-home" style={{ maxWidth: 900, margin: "26px auto 0" }}>
            <div style={{ textAlign: "center", marginBottom: 34 }}>
              <div style={{ fontSize: 11, color: "#64748B", fontWeight: 800, letterSpacing: 1.6 }}>ADMIN CONSOLE</div>
              <h1 data-testid="admin-home-heading" style={{ margin: "10px 0 0", color: "#0F1B2D", fontSize: 30, fontWeight: 800 }}>Manage C-ORE operations</h1>
            </div>
            <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2" style={{ gap: 16 }}>
              {ADMIN_SECTIONS.map((section) => (
                <FolderCard key={section.id} section={section} status={sectionStatus[section.id]} onOpen={() => setActiveSection(section.id)} />
              ))}
            </div>
          </div>
        ) : (
          <>
            <AdminBreadcrumb sectionName={activeSectionName} onHome={() => setActiveSection("home")} />

        {activeSection === "new-signups" && (
        <>
        {/* New Signups table */}
        <div style={tableCard}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B2D" }}>New signups</div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Click any row to open the setup checklist</div>
            </div>
            <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 10px" }}>
              {newSignups.length} awaiting
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="new-signups-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name & Company</th>
                  <th style={th}>Contact</th>
                  <th style={th}>Industry & City</th>
                  <th style={th}>Software</th>
                  <th style={th}>Revenue</th>
                  <th style={th}>Signed up</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {newSignups.length === 0 && (
                  <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: 24 }}>No new signups awaiting setup.</td></tr>
                )}
                {newSignups.map((c) => (
                  <tr key={c.id} data-testid={`new-row-${c.id}`} style={{ cursor: "pointer" }} onClick={() => setActiveCustomer(c)}>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div style={{ color: "#6B7280" }}>{c.company_name || "—"}</div>
                    </td>
                    <td style={td}>
                      <div>{c.phone}</div>
                      <div style={{ color: "#6B7280" }}>{c.email}</div>
                    </td>
                    <td style={td}>
                      <div>{c.industry || "—"}</div>
                      <div style={{ color: "#6B7280" }}>{c.city || "—"}{c.state ? `, ${c.state}` : ""}</div>
                    </td>
                    <td style={td}><SoftwarePills list={c.software_used} /></td>
                    <td style={td}>{c.revenue_range || "—"}</td>
                    <td style={td}>{timeAgo(c.created_at)}</td>
                    <td style={td}><StatusBadge status={c.status} /></td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <button data-testid={`setup-call-${c.id}`} onClick={(e) => { e.stopPropagation(); setActiveCustomer(c); }}
                        style={{ background: "#1E3A5F", color: "white", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        Setup Call →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}

        {activeSection === "all-customers" && (
        <>
        {/* All Customers table */}
        <div style={tableCard}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B2D" }}>All customers</div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>{filtered.length} matching · page {page} of {pageCount}</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ position: "relative" }}>
                <Search size={12} style={{ position: "absolute", left: 8, top: 9, color: "#9CA3AF" }} />
                <input data-testid="all-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, company, phone…"
                  className="w-[150px] tablet:w-[190px] desktop:w-[220px]"
                  style={{ height: 32, padding: "0 10px 0 24px", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 12 }} />
              </div>
              <select data-testid="all-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                style={{ height: 32, padding: "0 8px", border: "1px solid #D1D5DB", borderRadius: 6, fontSize: 12 }}>
                <option value="">All statuses</option>
                <option value="pending_otp">Pending OTP</option>
                <option value="verified">Verified</option>
                <option value="profile_complete">Profile complete</option>
                <option value="pending_setup">Pending setup</option>
                <option value="active">Active</option>
              </select>
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table data-testid="all-customers-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Name & Company</th>
                  <th style={th}>Contact</th>
                  <th style={th}>Status</th>
                  <th style={th}>Created</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: 24 }}>No customers match.</td></tr>
                )}
                {pageRows.map((c) => (
                  <tr key={c.id} data-testid={`all-row-${c.id}`} style={{ cursor: "pointer" }} onClick={() => setActiveCustomer(c)}>
                    <td style={td}>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div style={{ color: "#6B7280" }}>{c.company_name || "—"}</div>
                    </td>
                    <td style={td}>
                      <div>{c.phone}</div>
                      <div style={{ color: "#6B7280" }}>{c.email}</div>
                    </td>
                    <td style={td}><StatusBadge status={c.status} /></td>
                    <td style={td}>{timeAgo(c.created_at)}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button data-testid={`inject-task-${c.id}`} title="Inject task into Command Centre"
                          onClick={(e) => { e.stopPropagation(); setContextCustomer(c); }}
                          style={{ background: "white", color: "#1E3A5F", border: "1px solid #93C5FD", borderRadius: 6, padding: "5px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Sparkles size={12} /> Inject Task
                        </button>
                        <button data-testid={`upgrade-customer-${c.id}`} title="Upgrade customer"
                          onClick={(e) => { e.stopPropagation(); setUpgradeCustomer(c); }}
                          style={{ background: "white", color: "#047857", border: "1px solid #6EE7B7", borderRadius: 6, padding: "5px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <ArrowUpCircle size={12} /> Upgrade
                        </button>
                        <button data-testid={`delete-customer-${c.id}`} title="Delete customer"
                          onClick={(e) => { e.stopPropagation(); setLifecycle({ customer: c, mode: "delete" }); }}
                          style={{ background: "white", color: "#B91C1C", border: "1px solid #FCA5A5", borderRadius: 6, padding: "5px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "10px 14px", borderTop: "1px solid #E5E7EB" }}>
            <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ background: "white", border: "1px solid #D1D5DB", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.5 : 1 }}>← Prev</button>
            <button data-testid="page-next" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              style={{ background: "white", border: "1px solid #D1D5DB", borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: page >= pageCount ? "default" : "pointer", opacity: page >= pageCount ? 0.5 : 1 }}>Next →</button>
          </div>
        </div>
        </>
        )}

        {activeSection === "funding" && (
        <>
        {/* C-ORE Fund — funding advisory requests */}
        <div style={tableCard} data-testid="funding-requests-card">
          <div style={{ padding: "14px 18px", borderBottom: fundingRequests.length ? "1px solid #E5E7EB" : "none" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B2D" }}>C-ORE Fund — Funding Requests</div>
            <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Customers who requested funding advisory · consent recorded for audit</div>
          </div>
          {fundingRequests.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table data-testid="funding-requests-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Company & Contact</th>
                    <th style={th}>Contact</th>
                    <th style={th}>Consent</th>
                    <th style={th}>Status</th>
                    <th style={th}>Requested</th>
                  </tr>
                </thead>
                <tbody>
                  {fundingRequests.map((r) => (
                    <tr key={r.id} data-testid={`funding-row-${r.id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 700 }}>{r.company_name || "—"}</div>
                        <div style={{ color: "#6B7280" }}>{r.contact_name || "—"}</div>
                      </td>
                      <td style={td}>
                        <div>{r.phone || "—"}</div>
                        <div style={{ color: "#6B7280" }}>{r.email || "—"}</div>
                      </td>
                      <td style={td}>{r.consent ? <span style={{ color: "#047857", fontWeight: 700 }}>✓ Consented</span> : "—"}</td>
                      <td style={td}><span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: "#EFF6FF", color: "#1E3A5F" }}>{r.status}</span></td>
                      <td style={td}>{timeAgo(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {fundingRequests.length === 0 && (
            <div style={{ padding: "18px", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>No funding requests yet.</div>
          )}
        </div>
        </>
        )}

        {/* Subscription Management — admin-managed plan catalog */}
        {activeSection === "subscriptions" && <SubscriptionManagement />}

        {activeSection === "deleted-customers" && (
        <>
        {/* Deleted customers (soft-deleted, recoverable) */}
        <div style={tableCard} data-testid="deleted-customers-card">
          <div style={{ padding: "14px 18px", borderBottom: showDeleted ? "1px solid #E5E7EB" : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#0F1B2D" }}>Deleted customers</div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Soft-deleted · data retained · restore requires founder OTP</div>
            </div>
            <button data-testid="toggle-deleted" onClick={() => setShowDeleted((v) => !v)}
              style={{ background: "white", border: "1px solid #D1D5DB", borderRadius: 6, padding: "5px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", color: "#374151" }}>
              {showDeleted ? "Hide" : `Show (${deletedCustomers.length})`}
            </button>
          </div>
          {showDeleted && (
            <div style={{ overflowX: "auto" }}>
              <table data-testid="deleted-customers-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={th}>Name & Company</th>
                    <th style={th}>Contact</th>
                    <th style={th}>Deleted</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {deletedCustomers.length === 0 && (
                    <tr><td colSpan={4} style={{ ...td, textAlign: "center", color: "#9CA3AF", padding: 24 }}>No deleted customers.</td></tr>
                  )}
                  {deletedCustomers.map((c) => (
                    <tr key={c.id} data-testid={`deleted-row-${c.id}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 700 }}>{c.name}</div>
                        <div style={{ color: "#6B7280" }}>{c.company_name || "—"}</div>
                      </td>
                      <td style={td}>
                        <div>{c.phone}</div>
                        <div style={{ color: "#6B7280" }}>{c.email}</div>
                      </td>
                      <td style={td}>{timeAgo(c.deleted_at)}<div style={{ color: "#9CA3AF", fontSize: 11 }}>{c.deleted_by || ""}</div></td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <button data-testid={`restore-customer-${c.id}`}
                          onClick={() => setLifecycle({ customer: c, mode: "restore" })}
                          style={{ background: "white", color: "#047857", border: "1px solid #6EE7B7", borderRadius: 6, padding: "5px 8px", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <RotateCcw size={12} /> Restore
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {/* Tally Sync Health Monitor — below All Customers */}
        {activeSection === "tally-health" && <TallyHealthMonitor customers={allCustomers} onOpenChecklist={setActiveCustomer} />}
          </>
        )}
      </div>

      {activeCustomer && (
        <SetupChecklistModal customer={activeCustomer} onClose={() => setActiveCustomer(null)} onAfter={refresh} />
      )}
      {lifecycle && (
        <CustomerLifecycleModal
          customer={lifecycle.customer}
          mode={lifecycle.mode}
          onClose={() => setLifecycle(null)}
          onDone={() => { setLifecycle(null); refresh(); }}
        />
      )}
      {upgradeCustomer && (
        <CustomerUpgradeModal
          customer={upgradeCustomer}
          onClose={() => setUpgradeCustomer(null)}
          onDone={() => { setUpgradeCustomer(null); refresh(); }}
        />
      )}
      {contextCustomer && (
        <CustomerContextModal
          customer={contextCustomer}
          onClose={() => setContextCustomer(null)}
        />
      )}
    </div>
  );
}
